import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { GameScoreService } from "../lib/game-score-service";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Recupera la sessione corrente
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Se c'è una sessione attiva, assicurati che l'utente sia inizializzato
      if (session?.user) {
        const fullName = session.user.user_metadata?.full_name || "";
        const [firstName, ...lastNameParts] = fullName.split(" ");
        const lastName = lastNameParts.join(" ");

        await GameScoreService.initializeUser(
          session.user.id,
          session.user.email || "",
          firstName,
          lastName
        );
      }
    });

    // Ascolta i cambiamenti di autenticazione
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (event === "SIGNED_IN" && session?.user) {
        console.log("User signed in, initializing in database...");

        const fullName = session.user.user_metadata?.full_name || "";
        const [firstName, ...lastNameParts] = fullName.split(" ");
        const lastName = lastNameParts.join(" ");

        await GameScoreService.initializeUser(
          session.user.id,
          session.user.email || "",
          firstName,
          lastName
        );
      }
    });

    return () => subscription.unsubscribe();
  }, [mounted]);

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (error) {
      console.error("Error signing in with Google:", error);
      throw error;
    }
  };

  const signInWithFacebook = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "facebook",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) {
      console.error("Error signing in with Facebook:", error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();

      if (error && error.message !== "Auth session missing!") {
        console.error("Error signing out:", error);
      }
    } catch (error) {
      console.log("Logout session error (ignored):", error);
    } finally {
      // Pulisci localStorage e sessionStorage
      localStorage.removeItem("championId");
      localStorage.removeItem("login");
      localStorage.removeItem("acceptTerms");
      localStorage.removeItem("selectedChampion");
      localStorage.removeItem("playerName");
      localStorage.removeItem("playerEmail");
      localStorage.removeItem("userId");
      localStorage.removeItem("ranking");

      sessionStorage.removeItem("playerName");
      sessionStorage.removeItem("selectedChampion");
      sessionStorage.removeItem("playerEmail");
      sessionStorage.removeItem("userId");
      sessionStorage.removeItem("ranking");

      // Reimposta lo stato locale
      setUser(null);
      setSession(null);

      // Reindirizza alla home
      navigate("/");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signInWithGoogle,
        signInWithFacebook,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
